import get from 'lodash/get';
import dropRight from 'lodash/dropRight';
import last from 'lodash/last';
import sum from 'lodash/sum';
import { REMOTE_TRUE } from 'src/containers/AddSkuPage/constants';
import { TYPES } from './actions';
import {
  DEFAULT_GROUP,
  DIRECTION_NEXT,
  DIRECTION_PREVIOUS,
  SMART_HOME_PLAN,
  NO_PLAN,
  TRUCK_ROLL_FEE
} from './constants';

export const initialState = {
  currentQuestion: '',
  currentGroup: DEFAULT_GROUP,
  questionPath: [],
  tempAnswerQue: [],
  completedAnswers: [],
  previousSearch: '',
  currentSearch: '',
  maxFlow: 0,
  completionPercentage: 0,
  adjustedPrice: 0,
  skuName: '',
  skuBasePrice: 0,
  flowComplete: false,
  direction: DIRECTION_NEXT,
  redirect: false,
  breakdown({ planType = NO_PLAN, remotePossible } = {}) {
    /*
     * This is a way to get at the state and bundle for nice presentation.
     * Bundle Answers is just a concatenation of all array of answers under a question
    */
    let remoteLineItem;
    let isRemote = false;
    let modifiedBasePrice = planType === SMART_HOME_PLAN ? TRUCK_ROLL_FEE : +this.skuBasePrice;
    const bundleAnswers = [...this.tempAnswerQue, ...this.completedAnswers].reduce((bundle, v) => bundle.concat(v.answers), []);
    const lineItemsAndPrice = bundleAnswers.reduce((obj, answer) => {
      const dataObject = { ...obj };
      // Remember, answers are contained in an array since there can be more than 1 per Question.
      // group up the answers that have number values associated with them.
      if (!answer.showInput && !Number.isNaN(parseInt(answer.value, 10))) {
        const lineItem = {
          label: answer.label.replace(/\([^a-zA-Z]+\)$/, '').trim(),
          price: `${answer.value < 0 ? '-' : ''}$${Math.abs(answer.value) || 0}`,
          value: answer.value,
          id: answer.id,
        };

        if (answer.id === REMOTE_TRUE) {
          isRemote = true;
          remoteLineItem = lineItem;
          modifiedBasePrice += +lineItem.value;
          return dataObject;
        }

        dataObject.lineItems.push(lineItem);
        dataObject.totalPrice += +answer.value;
      }
      return dataObject;
    }, { totalPrice: modifiedBasePrice, lineItems: [] })

    // Calculate remote item if needed. If an adjustment was selected, we want to convert the item
    // to an in-home service if the remote option was selected.
    if (remoteLineItem && !lineItemsAndPrice.lineItems.length) {
      if (planType === NO_PLAN) {
        lineItemsAndPrice.totalPrice += remoteLineItem.value;
      } else {
        lineItemsAndPrice.totalPrice = 0;
        modifiedBasePrice = 0;
      }
    }

    // We have remote and a user selected an in-home addon. Switch to in-home
    if (remoteLineItem && lineItemsAndPrice.lineItems.length) {
      isRemote = false;
      modifiedBasePrice += Math.abs(remoteLineItem.value);
    }
    /*
    * @params lineItems: concat base price w/ answers
    * @params totalPrice: this is the sum of the base price + all line items
    * @params captionPostPend: when user does not have a plan, we add a note to the base price
    * @params showCaption: show when the plan is remotePossible
    * */
    const noPlan = planType === NO_PLAN;
    const captionPostPend = ` (${isRemote ? 'Remote' : 'In-Home'})`;
    const showCaption = remotePossible && captionPostPend || '';
    const label = noPlan ? `Base Price${showCaption}` : `Member's Price${showCaption}`;
    return {
      lineItems: [{ label, price: `$${modifiedBasePrice}` }, ...lineItemsAndPrice.lineItems],
      totalPrice: lineItemsAndPrice.totalPrice
    }
  }
}

export function answersReducer(state, action) {
  switch (action.type) {
    case TYPES.PAGE_LEVEL_ERROR: {
      // maybe show the alert error bar or ?
      return state;
    }
    case TYPES.UPDATE_PRICE: {
      const adjustedPrice = state.completedAnswers.reduce((ttl, {value = 0}) => +ttl + +value, 0);
      return {...state, adjustedPrice };
    }
    case TYPES.ADD_ANSWER: {
      const { answer } = action.payload;
      /* If we already have a same answer, remove it. And update with new selection. Also, only do it if we have an answer */
      const completedAnswers = Object.entries(answer.answers).length && state.completedAnswers.
        filter(
          completedAnswer => answer.answers.some(ans => completedAnswer.id !== ans.id && completedAnswer.qid !== answer.qid)
        ).concat({ ...answer}) || state.completedAnswers;
      /* User submitted last question/answer */
      const flowComplete = get(answer, 'complete', false);

      return {
        ...state,
        completedAnswers,
        flowComplete,
        tempAnswerQue: [],
        direction: DIRECTION_NEXT,
        completionPercentage: generatePercentage(completedAnswers, state.maxFlow),
      };
    }
    case TYPES.UPDATE_TO_NEW_QA: {
      const { qa, group = '' } = action.payload;
      if (!qa.id) return { ...state };

      const questionPath = state.questionPath.concat(qa);

      return {
        ...state,
        ...{
            currentQuestion: qa.id,
            questionPath,
            currentGroup: group || DEFAULT_GROUP,
            direction: DIRECTION_NEXT,
            tempAnswerQue: [],
            currentSearch: questionPath.length,
            previousSearch: Math.max(1, state.currentSearch),
        }
      };
    }

    case TYPES.GO_BACK: {
      /* remove current selections */
      const questionPath = dropRight(state.questionPath);
      const completedAnswers = dropRight(state.completedAnswers);
      const currentGroup = get(last(completedAnswers), 'answers.0.group', DEFAULT_GROUP);
      const currentQuestion = get(last(questionPath), 'id', '');
      const previousSearch = state.currentSearch;
      const currentSearch = Math.max(0, state.currentSearch - 1);

      return {
        ...state,
        questionPath,
        completedAnswers,
        currentGroup,
        currentQuestion,
        tempAnswerQue: [],
        direction: DIRECTION_PREVIOUS,
        previousSearch,
        currentSearch,
        redirect: !questionPath.length,
        completionPercentage: generatePercentage(completedAnswers, state.maxFlow),
      }
    }

    case TYPES.SET_BASE_SKU_INFORMATION: {
      const { skuName, skuBasePrice, maxFlow } = action.payload;
      return { ...state, skuBasePrice, skuName, maxFlow }
    }

    case TYPES.ADD_TEMP_ANSWER_TO_QUE: {
      const tempAnswerQue = state.tempAnswerQue.concat(get(action, 'payload.queObject', ''));

      return {...state, tempAnswerQue };
    }

    case TYPES.REMOVE_TEMP_ANSWER_TO_QUE: {
      const tempAnswerQue = state.tempAnswerQue.filter(({ answers = [] }) => (
        answers.some(item => {
          // eslint-disable-next-line
          return item.id != action.payload.id
        })
      ))

      return { ...state, tempAnswerQue }
    }

    default:
      return state;
  }
}

/*
* This cycles thru the answers and calculates the flowAdjustments based on user selections.
* */
function generatePercentage(answers = [], max) {
  const flowAdjustmentMax = max + sum(answers.map(answer => get(answer, 'answers.0.flowAdjustment', 0)));
  const percentage = Number.parseInt((answers.length/flowAdjustmentMax) * 100, 10);

  return Number.isFinite(percentage) && percentage || 0;
}
